import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { setPassword } from '@/api/services/auth.service'
import { Button, Card, Input } from '@/components/ui'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { PASSWORD_HINT, newPasswordSchema } from '@/lib/passwordPolicy'

const setPasswordSchema = z
  .object({
    newPassword: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>

export interface SetPasswordFormProps {
  token: string | null
}

export function SetPasswordForm({ token }: SetPasswordFormProps): React.ReactElement {
  const navigate = useNavigate()
  const [submitted, setSubmitted] = useState(false)
  const invalidLinkMessage = 'This reset link is invalid or expired. Please request a new one.'
  const [linkError, setLinkError] = useState<string | null>(token ? null : invalidLinkMessage)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const mutation = useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) =>
      setPassword({ token, newPassword }),
    onSuccess: () => {
      setLinkError(null)
      setSubmitted(true)
      window.setTimeout(() => {
        navigate('/login', { replace: true })
      }, 1200)
    },
    onError: (error) => {
      const responseStatus = (error as { response?: { status?: number } })?.response?.status
      const message = getErrorMessage(error)
      if (responseStatus === 400 && !/password/i.test(message)) {
        setLinkError(invalidLinkMessage)
        return
      }
      setLinkError(null)
    },
  })

  const onSubmit = (values: SetPasswordFormValues): void => {
    if (!token) {
      setLinkError(invalidLinkMessage)
      return
    }
    setLinkError(null)
    mutation.mutate({ token, newPassword: values.newPassword })
  }

  return (
    <Card
      className="w-full max-w-md border-gray-200 bg-white shadow-md dark:border-gray-200 dark:bg-white"
      contentClassName="p-5 sm:p-8"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Set your password</h2>
          <p className="mt-1 text-sm text-gray-500">
            Choose a new password to finish setting up your account.
          </p>
        </div>

        {submitted ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Password set successfully. You can now sign in.
          </div>
        ) : null}

        {linkError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {linkError}
          </div>
        ) : null}

        {!submitted ? (
          <>
            <Input
              label="New password"
              type="password"
              autoComplete="new-password"
              showPasswordToggle
              hint={PASSWORD_HINT}
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />
            <Input
              label="Confirm new password"
              type="password"
              autoComplete="new-password"
              showPasswordToggle
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
            {mutation.isError && !linkError ? (
              <p className="text-sm text-danger" role="alert">
                {getErrorMessage(mutation.error)}
              </p>
            ) : null}
            <Button type="submit" className="min-h-11 w-full" disabled={!token} loading={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Set password'}
            </Button>
          </>
        ) : null}

        <p className="text-center text-xs text-gray-500">
          Need help? Contact your administrator or return to{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            sign in
          </Link>
          .
        </p>
      </form>
    </Card>
  )
}

SetPasswordForm.displayName = 'SetPasswordForm'
